import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../services/api';

export interface Box {
  id: string;
  nom: string;
  code: string;
  zoneId?: string;
  zone?: {
    id: string;
    nom: string;
    code: string;
  };
  homeZoneId: string;
  homeZone: {
    id: string;
    nom: string;
    code: string;
  };
  createdAt: string;
  updatedAt: string;
  objets?: Array<{
    id: string;
    nom: string;
    quantite: number;
    etat: string;
  }>;
}

export const useBoxes = () => {
  return useQuery({
    queryKey: ['boxes'],
    queryFn: async (): Promise<Box[]> => {
      const response = await apiClient.get('/boxes');
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useBox = (id: string) => {
  return useQuery({
    queryKey: ['box', id],
    queryFn: async (): Promise<Box> => {
      const response = await apiClient.get(`/boxes/${id}`);
      return response.data;
    },
    enabled: !!id,
  });
};

export interface SuggestedObjet {
  id: string;
  nom: string;
  categorie?: string;
  sousCategorie?: string;
  etat?: string;
  zone?: { id: string; nom: string; theme?: string };
}

export interface SuggestedObjetsResponse {
  boxId: string;
  boxTheme?: string | null;
  objets: SuggestedObjet[];
  total: number;
  message?: string;
}

export const useSuggestedObjetsForBox = (boxId: string, enabled = true) => {
  return useQuery({
    queryKey: ['boxes', boxId, 'suggested-objets'],
    queryFn: async (): Promise<SuggestedObjetsResponse> => {
      const response = await apiClient.get(`/boxes/${boxId}/suggested-objets`);
      return response.data;
    },
    enabled: enabled && !!boxId,
    staleTime: 60 * 1000,
  });
};

export const useCreateBox = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<Box>) => {
      const response = await apiClient.post('/boxes', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['boxes'] });
    },
  });
};

export const useUpdateBox = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Box> }) => {
      const response = await apiClient.patch(`/boxes/${id}`, data);
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['boxes'] });
      queryClient.invalidateQueries({ queryKey: ['box', id] });
    },
  });
};

export const useDeleteBox = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/boxes/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['boxes'] });
    },
  });
};

export const useMoveBox = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      id, 
      nouvelleZoneId 
    }: { 
      id: string; 
      nouvelleZoneId: string; 
    }) => {
      const response = await apiClient.post(`/boxes/${id}/mouvement`, {
        nouvelleZoneId,
      });
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['boxes'] });
      queryClient.invalidateQueries({ queryKey: ['box', id] });
    },
  });
};

